import { ViewIds } from '@ardoq/api-types';
import { Features, hasFeature } from '@ardoq/features';
import { type ModeViews, viewpointModeViews$ } from '../modeViews$';
import { VIEWPOINT_MODE_VIEW_DEFINITIONS } from '../metaInfoConsts';
import type { ViewpointModeViewKey } from './viewSettings/types';
import { loadedGraph$ } from 'traversals/loadedGraph$';

/**
 * Whether the Flexible Block Diagram currently replaces the (modernized) Block
 * Diagram in viewpoint mode (ARD-33096). Single source of truth for the swap, so
 * the reroute and the picker filters that hide the standalone FBD entry cannot
 * drift apart.
 */
export const isFlexibleBlockDiagramStandingInForBlockDiagram = (): boolean =>
  hasFeature(Features.FLEXIBLE_BLOCK_DIAGRAM_AS_BLOCK_DIAGRAM);

/**
 * Maps a viewpoint's view id to the one actually rendered. When the Flexible
 * Block Diagram stands in for the Block Diagram, it renders in place of
 * MODERNIZED_BLOCK_DIAGRAM everywhere — live viewpoints and saved presentation
 * slides alike. The swap needs no settings conversion: Flexible Block Diagram
 * settings are a strict superset of Block Diagram's (ARD-33247). See
 * `renderedViewpointViewSettings`.
 */
export const toRenderedViewpointViewId = (viewId: ViewIds): ViewIds =>
  viewId === ViewIds.MODERNIZED_BLOCK_DIAGRAM &&
  isFlexibleBlockDiagramStandingInForBlockDiagram()
    ? ViewIds.FLEXIBLE_BLOCK_DIAGRAM
    : viewId;

/**
 * The inverse of {@link toRenderedViewpointViewId}: the view id to persist for a
 * rendered one. The swap is a render-time concern, so a viewpoint or slide saved
 * while the Flexible Block Diagram stands in for the Block Diagram keeps storing
 * MODERNIZED_BLOCK_DIAGRAM. That is what makes turning the flag back off lossless
 * (ARD-33096), and it keeps the stored id one the view-style pickers offer — they
 * hide the standalone Flexible Block Diagram entry while the swap is on, so a
 * viewpoint persisted with it has no selectable style (ARD-33402).
 *
 * Viewpoint mode only, unlike its inverse. The Flexible Block Diagram is a
 * workspace-mode view in its own right, so mapping it there would store a view
 * that mode replaces with the legacy Block Diagram. Its inverse carries no such
 * gate: MODERNIZED_BLOCK_DIAGRAM cannot be a workspace-mode tab, but a legacy
 * bookmark or slide can still hold it, and those are mapped in either mode.
 */
export const toStoredViewpointViewId = (viewId: ViewIds): ViewIds =>
  viewId === ViewIds.FLEXIBLE_BLOCK_DIAGRAM &&
  // oxlint-disable-next-line ardoq-lint/no-direct-stream-state-access
  loadedGraph$.state.isViewpointMode &&
  isFlexibleBlockDiagramStandingInForBlockDiagram()
    ? ViewIds.MODERNIZED_BLOCK_DIAGRAM
    : viewId;

/**
 * The view id to persist when saving over an existing viewpoint: the id the swap
 * would store, except that a viewpoint already holding the standalone Flexible
 * Block Diagram keeps it. While the swap is on the picker hides that entry, so
 * mapping it to the Block Diagram would rewrite a view style the user chose and
 * that they have no way to choose again.
 */
export const toUpdatedViewpointViewId = (
  viewIdToStore: ViewIds,
  existingViewId: ViewIds
): ViewIds =>
  viewIdToStore === ViewIds.MODERNIZED_BLOCK_DIAGRAM &&
  existingViewId === ViewIds.FLEXIBLE_BLOCK_DIAGRAM &&
  isFlexibleBlockDiagramStandingInForBlockDiagram()
    ? existingViewId
    : viewIdToStore;

/**
 * Whether a view should be dropped from the viewpoint picker because the swap
 * makes it reachable through another entry. The standalone Flexible Block Diagram
 * is hidden while it stands in for the Block Diagram — the Block Diagram card
 * reroutes to it via `toRenderedViewpointViewId`.
 */
export const isViewHiddenByBlockDiagramSwap = (viewId: ViewIds): boolean =>
  viewId === ViewIds.FLEXIBLE_BLOCK_DIAGRAM &&
  isFlexibleBlockDiagramStandingInForBlockDiagram();

/**
 * A viewpoint-mode view picker entry: the id the card is labelled by, and the id
 * actually rendered/selected when it is picked. They differ only when a view
 * stands in for another (the Flexible Block Diagram for the Block Diagram).
 */
export type ViewPickerViewId = { viewId: ViewIds; renderedViewId: ViewIds };

/**
 * The viewpoint-mode view picker's entries: the given views minus any hidden by
 * the block-diagram swap, each paired with the id it actually renders as.
 */
export const toViewPickerViewIds = (viewIds: ViewIds[]): ViewPickerViewId[] =>
  viewIds
    .filter(viewId => !isViewHiddenByBlockDiagramSwap(viewId))
    .map(viewId => ({
      viewId,
      renderedViewId: toRenderedViewpointViewId(viewId),
    }));

export const DEFAULT_TRAVERSAL_VIEW_ID = ViewIds.MODERNIZED_BLOCK_DIAGRAM;

export const toSupportedTraversalViewIdOrDefault = (
  viewpointModeViews: ModeViews<ViewpointModeViewKey>,
  viewId: ViewIds | undefined
): ViewIds => {
  if (!viewId) {
    return DEFAULT_TRAVERSAL_VIEW_ID;
  }
  if (!Object.hasOwn(viewpointModeViews.byIds, viewId)) {
    return DEFAULT_TRAVERSAL_VIEW_ID;
  }
  return viewId;
};

export const getSupportedTraversalViewIdOrDefault = (
  viewId?: ViewIds
): ViewIds =>
  toSupportedTraversalViewIdOrDefault(viewpointModeViews$.state, viewId);

type ViewpointModeViewDefinitions =
  (typeof VIEWPOINT_MODE_VIEW_DEFINITIONS)[number];
type ViewsWithoutFullMultiLabelsSupport = Extract<
  ViewpointModeViewDefinitions,
  { fullMultiLabelSupport: false }
>['id'];
type ViewsWithoutFullGroupingSupport = Extract<
  ViewpointModeViewDefinitions,
  { fullGroupingSupport: false }
>['id'];

export const isViewWithoutFullyMultiLabelsSupport = (
  viewId: ViewIds
): viewId is ViewsWithoutFullMultiLabelsSupport =>
  VIEWPOINT_MODE_VIEW_DEFINITIONS.find(({ id }) => id === viewId)
    ?.fullMultiLabelSupport === false;
export const isViewWithoutFullyGroupingSupport = (
  viewId: ViewIds
): viewId is ViewsWithoutFullGroupingSupport =>
  VIEWPOINT_MODE_VIEW_DEFINITIONS.find(({ id }) => id === viewId)
    ?.fullGroupingSupport === false;
